/**
 * Control Portal - Global Alerts Page
 * Platform-wide alerts monitoring across all tenants
 */
import { useState, useEffect } from 'react';
import { controlApi } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Bell, AlertTriangle, Thermometer, Wifi, WifiOff, Battery,
  RefreshCw, Loader2, Search, CheckCircle, XCircle, Clock, Building2
} from 'lucide-react';

export default function GlobalAlertsPage() {
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [summary, setSummary] = useState({ total: 0, critical: 0, warning: 0, resolved: 0 });
  const [severityFilter, setSeverityFilter] = useState('all');
  const [tenantFilter, setTenantFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchAlerts();
  }, []);

  const fetchAlerts = async () => {
    // Demo data for global alerts across all tenants
    const demoAlerts = [
      { id: 1, tenant: 'Apollo Pharmacy', device: 'INV-DEV-0001', type: 'TEMP_BREACH', severity: 'critical', message: 'Temperature exceeded max threshold (12°C > 8°C)', timestamp: '2026-03-04T06:30:00Z', status: 'active' },
      { id: 2, tenant: 'Apollo Pharmacy', device: 'INV-DEV-0003', type: 'DEVICE_OFFLINE', severity: 'warning', message: 'Device offline for 45 minutes', timestamp: '2026-03-04T06:15:00Z', status: 'active' },
      { id: 3, tenant: 'Tata 1mg', device: 'INV-DEV-0008', type: 'TEMP_BREACH', severity: 'critical', message: 'Temperature below min threshold (-2°C < 2°C)', timestamp: '2026-03-04T05:45:00Z', status: 'active' },
      { id: 4, tenant: 'Tata 1mg', device: 'INV-DEV-0009', type: 'LOW_BATTERY', severity: 'warning', message: 'Battery level critical (8%)', timestamp: '2026-03-04T05:30:00Z', status: 'active' },
      { id: 5, tenant: 'Medplus', device: 'INV-DEV-0012', type: 'DEVICE_OFFLINE', severity: 'warning', message: 'Device offline for 2 hours', timestamp: '2026-03-04T04:00:00Z', status: 'acknowledged' },
      { id: 6, tenant: 'Apollo Pharmacy', device: 'INV-DEV-0002', type: 'TEMP_BREACH', severity: 'critical', message: 'Temperature exceeded max threshold (10°C > 8°C)', timestamp: '2026-03-04T03:00:00Z', status: 'resolved' },
      { id: 7, tenant: 'PharmEasy', device: 'INV-DEV-0015', type: 'LICENSE_EXPIRING', severity: 'info', message: 'License expires in 7 days', timestamp: '2026-03-04T02:00:00Z', status: 'active' },
      { id: 8, tenant: 'Netmeds', device: 'INV-DEV-0018', type: 'GEOFENCE_BREACH', severity: 'warning', message: 'Device left designated delivery zone', timestamp: '2026-03-04T01:30:00Z', status: 'resolved' },
      { id: 9, tenant: 'Medplus', device: 'INV-DEV-0013', type: 'TEMP_BREACH', severity: 'critical', message: 'Temperature spike detected (15°C)', timestamp: '2026-03-03T23:00:00Z', status: 'resolved' },
      { id: 10, tenant: 'Apollo Pharmacy', device: 'INV-DEV-0005', type: 'LOW_BATTERY', severity: 'warning', message: 'Battery level low (15%)', timestamp: '2026-03-03T22:00:00Z', status: 'resolved' },
    ];
    
    setAlerts(demoAlerts);
    setSummary({
      total: demoAlerts.length,
      critical: demoAlerts.filter(a => a.severity === 'critical').length,
      warning: demoAlerts.filter(a => a.severity === 'warning').length,
      resolved: demoAlerts.filter(a => a.status === 'resolved').length
    });
    setLoading(false);
  };

  const getSeverityBadge = (severity) => {
    const styles = {
      critical: 'bg-red-500/20 text-red-400 border-red-500/30',
      warning: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      info: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    };
    return <Badge className={styles[severity]}>{severity}</Badge>;
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-red-500/20 text-red-400 border-red-500/30',
      acknowledged: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      resolved: 'bg-green-500/20 text-green-400 border-green-500/30',
    };
    return <Badge className={styles[status]}>{status}</Badge>;
  };

  const getAlertIcon = (type) => {
    switch (type) {
      case 'TEMP_BREACH': return <Thermometer className="w-4 h-4 text-red-400" />;
      case 'DEVICE_OFFLINE': return <WifiOff className="w-4 h-4 text-amber-400" />;
      case 'LOW_BATTERY': return <Battery className="w-4 h-4 text-amber-400" />;
      case 'LICENSE_EXPIRING': return <Clock className="w-4 h-4 text-blue-400" />;
      case 'GEOFENCE_BREACH': return <AlertTriangle className="w-4 h-4 text-amber-400" />;
      default: return <Bell className="w-4 h-4 text-slate-400" />;
    }
  };

  const filteredAlerts = alerts.filter(alert => {
    if (severityFilter !== 'all' && alert.severity !== severityFilter) return false;
    if (tenantFilter !== 'all' && alert.tenant !== tenantFilter) return false;
    if (searchQuery && !alert.message.toLowerCase().includes(searchQuery.toLowerCase()) && 
        !alert.device.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const tenants = [...new Set(alerts.map(a => a.tenant))];

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Bell className="w-8 h-8 text-amber-500" />
            Global Alerts
          </h1>
          <p className="text-slate-400 mt-1">Monitor alerts across all tenants</p>
        </div>
        <Button onClick={fetchAlerts} className="bg-blue-600 hover:bg-blue-700">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
          <div className="flex items-center gap-3 mb-2">
            <Bell className="w-5 h-5 text-blue-400" />
            <span className="text-slate-400 text-sm">Total Alerts</span>
          </div>
          <p className="text-2xl font-bold text-white">{summary.total}</p>
        </div>
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
          <div className="flex items-center gap-3 mb-2">
            <XCircle className="w-5 h-5 text-red-400" />
            <span className="text-slate-400 text-sm">Critical</span>
          </div>
          <p className="text-2xl font-bold text-red-400">{summary.critical}</p>
        </div>
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
          <div className="flex items-center gap-3 mb-2">
            <AlertTriangle className="w-5 h-5 text-amber-400" />
            <span className="text-slate-400 text-sm">Warnings</span>
          </div>
          <p className="text-2xl font-bold text-amber-400">{summary.warning}</p>
        </div>
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
          <div className="flex items-center gap-3 mb-2">
            <CheckCircle className="w-5 h-5 text-green-400" />
            <span className="text-slate-400 text-sm">Resolved</span>
          </div>
          <p className="text-2xl font-bold text-green-400">{summary.resolved}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-500 w-4 h-4" />
          <Input
            placeholder="Search alerts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-slate-900 border-slate-800 text-white"
          />
        </div>
        <Select value={severityFilter} onValueChange={setSeverityFilter}>
          <SelectTrigger className="w-40 bg-slate-900 border-slate-800 text-white">
            <SelectValue placeholder="Severity" />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-800">
            <SelectItem value="all">All Severity</SelectItem>
            <SelectItem value="critical">Critical</SelectItem>
            <SelectItem value="warning">Warning</SelectItem>
            <SelectItem value="info">Info</SelectItem>
          </SelectContent>
        </Select>
        <Select value={tenantFilter} onValueChange={setTenantFilter}>
          <SelectTrigger className="w-48 bg-slate-900 border-slate-800 text-white">
            <SelectValue placeholder="Tenant" />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-800">
            <SelectItem value="all">All Tenants</SelectItem>
            {tenants.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Alerts Table */}
      <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-slate-800">
              <TableHead className="text-slate-400">Type</TableHead>
              <TableHead className="text-slate-400">Tenant</TableHead>
              <TableHead className="text-slate-400">Device</TableHead>
              <TableHead className="text-slate-400">Message</TableHead>
              <TableHead className="text-slate-400">Severity</TableHead>
              <TableHead className="text-slate-400">Status</TableHead>
              <TableHead className="text-slate-400">Time</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredAlerts.map((alert) => (
              <TableRow key={alert.id} className="border-slate-800">
                <TableCell>{getAlertIcon(alert.type)}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-slate-500" />
                    <span className="text-slate-300">{alert.tenant}</span>
                  </div>
                </TableCell>
                <TableCell className="text-white font-mono text-sm">{alert.device}</TableCell>
                <TableCell className="text-slate-300 max-w-xs truncate">{alert.message}</TableCell>
                <TableCell>{getSeverityBadge(alert.severity)}</TableCell>
                <TableCell>{getStatusBadge(alert.status)}</TableCell>
                <TableCell className="text-slate-400 text-sm">
                  {new Date(alert.timestamp).toLocaleString()}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
